import React, { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { StakeholderDto } from "@green0meter/api-client";
import DOMPurify from "dompurify";

import DeleteConfirmationDialog from "../DeleteConfirmationDialog/DeleteConfirmationDialog";
import StakeholdersTable from "./components/StakeholdersTable";
import StakeholdersEditor from "./components/StakeholderEditor";

import { useUserStore } from "@store/user";
import { useDeleteStakeholder } from "@api";
const Stakeholders: FC = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [selectedStakeholder, setSelectedStakeholder] =
    useState<StakeholderDto | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [stakeholderToDelete, setStakeholderToDelete] =
    useState<StakeholderDto | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    deleteStakeholderAsync,
    isPendingDeleteStakeholder,
    isSuccessDeleteStakeholder,
  } = useDeleteStakeholder(user);

  const handleStakeholderEdit = (stakeholder: StakeholderDto): void => {
    setSelectedStakeholder(stakeholder);
    setIsEditorOpen(true);
  };

  const handleStakeholderDelete = (stakeholder: StakeholderDto): void => {
    setStakeholderToDelete(stakeholder);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!stakeholderToDelete) return;

    await deleteStakeholderAsync(stakeholderToDelete.id);
  };

  const handleDeleteCancel = (): void => {
    setShowDeleteDialog(false);
    setStakeholderToDelete(null);
  };

  const handleEditorClose = (): void => {
    setIsEditorOpen(false);
    setSelectedStakeholder(null);
  };

  const handleAddStakeholder = (): void => {
    setSelectedStakeholder(null);
    setIsEditorOpen(true);
  };

  useEffect(() => {
    if (isSuccessDeleteStakeholder) {
      setShowDeleteDialog(false);
      setStakeholderToDelete(null);
    }
  }, [isSuccessDeleteStakeholder]);

  return (
    <>
      <StakeholdersTable
        onStakeholderEdit={handleStakeholderEdit}
        onStakeholderDelete={handleStakeholderDelete}
        onAddStakeholder={handleAddStakeholder}
      />

      <StakeholdersEditor
        open={isEditorOpen}
        onClose={handleEditorClose}
        stakeholder={selectedStakeholder}
      />

      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("stakeholders.deleteDialog.title")}
        description={t(
          "stakeholders.deleteDialog.message",
          "Are you sure you want to remove this stakeholder? This action cannot be undone.",
        )}
        details={
          stakeholderToDelete ? (
            <>
              <strong
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(stakeholderToDelete.fullName),
                }}
              />{" "}
              ({stakeholderToDelete.email})
            </>
          ) : undefined
        }
        isLoading={isPendingDeleteStakeholder}
        confirmDisabled={isPendingDeleteStakeholder}
        confirmText={t("common.remove")}
      />
    </>
  );
};

export default Stakeholders;
